// Alone-time sessions, read alongside the camera: how much he barked, and
// how long it took him to settle. Pure.
//
// "Settled" = ten minutes with no detections from the camera. Without a
// camera we can't tell, so we fall back to what you ticked (settled or not).

import { MINUTE } from './time.ts';
import type { LogEvent, Millis } from './types.ts';

export const QUIET_MINUTES = 10;

/** Barking and crying/whining both count as "barks" in the session read-out. */
const vocal = (kind: string) => kind === 'bark' || kind === 'cry';

export interface AloneSession {
  id: string;
  start: Millis;
  end: Millis;
  minutes: number;
  barks: number;
  /** Other noise and movement the camera picked up. */
  other: number;
  /** Minutes until ten quiet minutes began; undefined = never settled (or no camera). */
  settledAfter?: number;
  /** Camera-based when there is one, else what was ticked. */
  settled?: boolean;
}

/** Has any camera detection ever been logged? (Then silence means quiet, not "no camera".) */
export const cameraSeen = (events: LogEvent[]) => events.some((e) => e.type === 'camera');

function detectionsBetween(events: LogEvent[], from: Millis, to: Millis): LogEvent<'camera'>[] {
  return events.filter((e): e is LogEvent<'camera'> => e.type === 'camera' && e.at >= from && e.at <= to && e.data.kind !== 'person').sort((a, b) => a.at - b.at);
}

/** When the first quiet stretch of QUIET_MINUTES began, in minutes from the start. */
export function settledAfter(start: Millis, end: Millis, times: Millis[]): number | undefined {
  let t = start;
  for (const d of [...times].sort((a, b) => a - b)) {
    if (d - t >= QUIET_MINUTES * MINUTE) break;
    t = d;
  }
  return end - t >= QUIET_MINUTES * MINUTE ? Math.round((t - start) / MINUTE) : undefined;
}

/** Finished alone sessions, oldest first. */
export function aloneSessions(events: LogEvent[], limit = 20): AloneSession[] {
  const camera = cameraSeen(events);
  return events
    .filter((e): e is LogEvent<'alone'> => e.type === 'alone' && e.end !== undefined)
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
    .reverse()
    .map((e) => {
      const end = e.end!;
      const dets = detectionsBetween(events, e.at, end);
      const after = camera ? settledAfter(e.at, end, dets.map((d) => d.at)) : undefined;
      return {
        id: e.id,
        start: e.at,
        end,
        minutes: Math.round((end - e.at) / MINUTE),
        barks: dets.filter((d) => vocal(d.data.kind)).length,
        other: dets.filter((d) => !vocal(d.data.kind)).length,
        settledAfter: after,
        settled: e.data.settled ?? (camera && end - e.at >= QUIET_MINUTES * MINUTE ? after !== undefined : undefined),
      };
    });
}

/** While he's alone now: how long it's been quiet, and the last thing heard. */
export function quietNow(open: LogEvent, events: LogEvent[], now: Millis): { quietMinutes: number; last?: LogEvent<'camera'>; barks: number; settled: boolean } {
  const dets = detectionsBetween(events, open.at, now);
  const last = dets.at(-1);
  const quietMinutes = Math.floor((now - (last?.at ?? open.at)) / MINUTE);
  return { quietMinutes, last, barks: dets.filter((d) => vocal(d.data.kind)).length, settled: quietMinutes >= QUIET_MINUTES };
}
